import {Badge, Button, Col, ModalBody, ModalFooter, Row, Spinner, Tab, Table, Tabs} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCancel, faCircleArrowDown, faEdit, faEye, faKey, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import {RREmployeesType, RREmployeeType} from "../../types/RREmployeeType";
import LoadDataFromServerError from "../../components/LoadDataFromServerError";
import {useAppSelector} from "../../hooks";
import {selectAllEmployees, selectEmployeesStatus} from "../../../features/AppDataSlice";
import {useEffect, useMemo, useState} from "react";
import {dateToString, showErrorsListInToast, zeroToEmptyStringAsNumber} from "../../tools";
import {
    RRDEPARTMENT_CETARING,
    RRDEPARTMENT_MONTAGE,
    RRDEPARTMENT_MONTAGE_LABEL,
    RRDepartmentType,
    RRDEPARTMENT_CETARING_LABEL, RRDEPARTMENT_STORE, RRDEPARTMENT_STORE_LABEL
} from "../../types/RRDepartmentType";
import RRTable from "../../components/rr-table/RRTable";
import {DownArrowIconButton, UpArrowIconButton} from "../../components/IconButtons";
import CenteredColumn from "../../components/CenteredColumn";
import {appFirestore} from "../../../index";
import {writeBatch} from "firebase/firestore";
import {selectCurrentEmployee, selectDepartment, selectDepartmentName} from "../../../features/AppStateSlice";

export interface Props {
    isVisible: boolean;
    onAdd: () => any;
    onEdit: (item: RREmployeeType) => any;
    onAuth: (item: RREmployeeType) => any;
    onDelete: (item: RREmployeeType) => any;
    onClose: (result: any) => any;
    onSelect?: (result: any) => any;
}

function DepartmentAsString(props: { department: RRDepartmentType }) {
    if (props.department === RRDEPARTMENT_MONTAGE) {
        return <>{RRDEPARTMENT_MONTAGE_LABEL}</>;
    }
    if (props.department === RRDEPARTMENT_CETARING) {
        return <>{RRDEPARTMENT_CETARING_LABEL}</>;
    }
    if (props.department === RRDEPARTMENT_STORE) {
        return <>{RRDEPARTMENT_STORE_LABEL}</>;
    }
    return null;
}

export function EmployeesList({isVisible, onAdd, onEdit, onAuth, onDelete, onClose, onSelect}: Props) {
    const department = useAppSelector(selectDepartment);
    const departmentName = useAppSelector(selectDepartmentName);
    const allDepEmployees = useAppSelector(selectAllEmployees);
    const allEmployees = allDepEmployees.filter(e =>
        e && (
            (department === "montage" && e.isMontage) ||
            (department === "catering" && e.isCatering) ||
            (department === "store" && e.isStore) ||
            (department === "logistics" && e.isLogistics)
        )
    );
    const employeesStatus = useAppSelector(selectEmployeesStatus);
    const errorEmployees = employeesStatus.error;
    const [employees, setEmployees] = useState<RREmployeesType>([]);
    const [listMode, setListMode] = useState<string>("ACTIVE");
    const [departMode, setDepartMode] = useState<string>("MONTAGE");
    const [working, setWorking] = useState(false);
    const [namesFilter, setNamesFilter] = useState("*");
    const currentEmpl = useAppSelector(selectCurrentEmployee);

    useEffect(() => {
        const today = new Date();
        setEmployees(
            allEmployees.filter(
                value => {
                    if (value.isDeleted) {
                        return false;
                    }

                    let result = false;
                    if (listMode === "ACTIVE") {
                        result = (!value.leaveDate || value.leaveDate >= today)
                    }

                    if (listMode === "ARCHIVE") {
                        result = (value.leaveDate && value.leaveDate < today) || false
                    }

                    if (result) {
                        result = value.department === departMode;
                    }

                    return result;
                }
            )
        );
    }, [allDepEmployees, listMode, departMode]);

    function isLoading() {
        return employeesStatus.loading;
    }

    const firstLetters = useMemo(() => {
        const _letters = new Set<string>();
        _letters.add("*");
        employees.forEach(e => e && e.name !== "DEV" && _letters.add(e.name.slice(0, 1)));
        return Array.from(_letters).sort((a, b) => a.localeCompare(b));
    }, [employees]);

    if (!isVisible) {
        return null;
    }

    async function move(d: RREmployeeType, offset: number) {
        const emplIds = employees.map(e => e.id);
        const emplIdx = emplIds.indexOf(d.id);
        if (emplIdx + offset <= 0 || emplIdx + offset > emplIds.length - 1) return;
        emplIds[emplIdx] = emplIds[emplIdx + offset];
        emplIds[emplIdx + offset] = d.id;

        const batch = writeBatch(appFirestore);
        try {
            setWorking(true);
            emplIds.forEach(
                (id, idx) => {
                    const item = employees.find(e => e.id === id);
                    if (item && item[`listIndex_${department || "montage"}`] !== idx) {
                        const data: any = {};
                        data[`listIndex_${department}`] = idx;
                        batch.update(item.ref!, data);
                    }
                }
            );
            await batch.commit();
        } catch (e: any) {
            showErrorsListInToast("Грешка", [e.message()]);
        } finally {
            setWorking(false)
        }
    }

    const employeesLength = employees.length - 1;

    if (!department) {
        return null;
    }

    return (
        <>
            <ModalBody>
                <Tabs activeKey={listMode}
                      onSelect={eventKey => setListMode(eventKey || "ACTIVE")}>
                    <Tab title={"Активни"} eventKey={"ACTIVE"}/>
                    <Tab title={"Напуснали"} eventKey={"ARCHIVE"}/>
                </Tabs>

                <Row className={"justify-content-between"}>
                    {
                        department === "montage" &&
                        <Col xs={"auto"}>
                            <Tabs activeKey={departMode} variant={"pills"} className={"mt-3 mb-2"}
                                  onSelect={eventKey => setDepartMode(eventKey || "MONTAGE")}>
                                <Tab title={departmentName} eventKey={"MONTAGE"}/>
                                <Tab title={"Кухня"} eventKey={"WAREHOUSE"}/>
                            </Tabs>
                        </Col>
                    }
                    <Col/>
                    <Col xs={"auto"}>
                        <Tabs activeKey={namesFilter} variant={"pills"} className={"mt-3 mb-2"}
                              onSelect={eventKey => setNamesFilter(eventKey || "*")}>
                            {
                                firstLetters.map(
                                    l => <Tab key={l} title={l} eventKey={l}/>
                                )
                            }
                        </Tabs>
                    </Col>
                </Row>


                {isLoading() && <Spinner animation={"border"}/>}

                {
                    !isLoading() && errorEmployees && <LoadDataFromServerError/>
                }

                {
                    !isLoading() && employees && employees.length === 0 &&
                    <Row className={"m-5"}>
                        <Col className={"text-center fw-bold"}>Няма дефинирани служители</Col>
                    </Row>
                }


                {
                    !isLoading() && !errorEmployees && employees && employees.length > 0 &&
                    <>
                        <div className={"max-h-50vh scrollable"}>
                            <RRTable>
                                <thead>
                                <tr>
                                    <th></th>
                                    <th>Име</th>
                                    <th className={"text-center"}>Отдел</th>
                                    <th className={"text-center"}>Права</th>
                                    <th>Ставки</th>
                                    <th>Назначен на</th>
                                    {listMode === "ARCHIVE" && <th>Напуснал на</th>}
                                    {listMode === "ACTIVE" &&
                                        <CenteredColumn width={"75px"} header>&nbsp;</CenteredColumn>}
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td></td>
                                </tr>
                                {
                                    employees.length > 0 &&
                                    employees
                                        .filter(e => e && (namesFilter === "*" || e.name.startsWith(namesFilter)))
                                        .map(
                                            (d, idx) =>
                                                <tr key={d.id} className={"tr-bordered lh-2em"}>
                                                    <td className={currentEmpl?.isSuperAdmin ? "w-100px" : "w-75px"}>
                                                        <Button size={"sm"} variant={"outline-secondary"}
                                                                onClick={() => onEdit(d)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit}/>
                                                        </Button>&nbsp;
                                                        {
                                                            currentEmpl?.isSuperAdmin &&
                                                            <>
                                                                <Button size={"sm"} variant={"outline-primary"}
                                                                        onClick={() => onAuth(d)}>
                                                                    <FontAwesomeIcon icon={faKey}/>
                                                                </Button>
                                                                &nbsp;
                                                            </>
                                                        }
                                                        {/*<Button size={"sm"} variant={"outline-danger"}*/}
                                                        {/*        onClick={() => {*/}
                                                        {/*            onDelete(d)*/}
                                                        {/*        }}*/}
                                                        {/*>*/}
                                                        {/*    <FontAwesomeIcon icon={faTrash}/>*/}
                                                        {/*</Button>&nbsp;*/}
                                                        {
                                                            onSelect &&
                                                            <Button size={"sm"} variant={"outline-success"}
                                                                    onClick={() => {
                                                                        onSelect(d)
                                                                    }}
                                                            >
                                                                <FontAwesomeIcon icon={faCircleArrowDown}/>
                                                            </Button>
                                                        }
                                                    </td>
                                                    <td className={"td-left-dbl-border"}> {d.name} </td>
                                                    <td className={"td-left-dbl-border text-center"}>
                                                        <Badge bg={d.isMontage ? "primary" : "secondary"}
                                                               title={d.isMontage ? "Монтаж" : "Не е в отдел монтаж"}
                                                        >М</Badge>&nbsp;
                                                        <Badge bg={d.isCatering ? "primary" : "secondary"}
                                                               title={d.isCatering ? "Кетъринг" : "Не е в отдел кетъринг"}
                                                        >C</Badge>&nbsp;
                                                        <Badge bg={d.isStore ? "primary" : "secondary"}
                                                                title={d.isStore ? "Кухня" : "Не е в отдел Кухня"}
                                                        >К</Badge>&nbsp;
                                                        <Badge bg={d.isLogistics ? "primary" : "secondary"}
                                                               title={d.isLogistics ? "Транспорт" : "Не е в отдел Транспорт"}
                                                        >Т</Badge>&nbsp;
                                                    </td>
                                                    <td className={"td-left-dbl-border text-center"}>
                                                        <Badge bg={d.isSuperAdmin ? "primary" : "secondary"}
                                                               title={d.isSuperAdmin ? "Администратор" : "Не е администратор"}
                                                        >A</Badge>&nbsp;
                                                        <Badge bg={d.isAdmin ? "primary" : "secondary"}
                                                               title={d.isAdmin ? "Оператор" : "Не е оператор"}
                                                        >О</Badge>&nbsp;
                                                        <Badge bg={d.isEmployee ? "primary" : "secondary"}
                                                               title={d.isEmployee ? "Служител" : "Не е служител"}
                                                        >C</Badge>&nbsp;
                                                        <Badge bg={d.canSeePayment ? "primary" : "secondary"}
                                                               title={(d.canSeePayment ? "Може" : "Не може") + " да вижда прихода си"}
                                                        >
                                                            <FontAwesomeIcon icon={faEye}/>
                                                        </Badge>&nbsp;

                                                    </td>
                                                    <td className={"td-left-dbl-border"}>
                                                        {zeroToEmptyStringAsNumber(d.pph, ' €/ч')} |&nbsp;
                                                        {zeroToEmptyStringAsNumber(d.supervisor_pph, ' €/ч')}
                                                    </td>
                                                    <td className={"td-left-dbl-border"}> {dateToString(d.startDate)} </td>
                                                    {listMode === "ARCHIVE" &&
                                                        <td className={"td-left-dbl-border"}> {dateToString(d.leaveDate)} </td>}
                                                    {listMode === "ACTIVE" &&
                                                        <CenteredColumn className={"td-left-dbl-border"}>
                                                            <UpArrowIconButton small onClick={() => move(d, -1)}
                                                                               disabled={idx === 0 || working || namesFilter !== "*"}/>
                                                            <DownArrowIconButton small onClick={() => move(d, 1)}
                                                                                 disabled={idx === employeesLength || working || namesFilter !== "*"}/>
                                                        </CenteredColumn>}
                                                </tr>
                                        )
                                }
                                </tbody>

                            </RRTable>
                        </div>
                    </>
                }
            </ModalBody>

            <ModalFooter>
                <Button onClick={() => onAdd()}>
                    <FontAwesomeIcon icon={faPlus}/> Добави нов
                </Button>
                <Button variant={"outline-secondary"} onClick={() => {
                    onClose(null)
                }}>
                    <FontAwesomeIcon icon={faCancel}/> Затвори
                </Button>
            </ModalFooter>
        </>
    )
}
